import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BrainOrb, BrainPrompt } from './primitives';
import { useBrain } from '../lib/brain';
import { useActiveTeam } from '../lib/queries/useActiveTeam';
import { useAuth } from '../contexts/AuthContext';
import { useVoiceToText } from '../lib/voiceInput';
import { useBrainAutoSpeak } from '../lib/useBrainAutoSpeak';
import { DraftCard } from './DraftCard';
import { supabase } from '../lib/supabase';
import './BrainChatPanel.css';

interface BrainChatPanelProps {
  open: boolean;
  onClose: () => void;
  /** Initial message to send to Brain when the panel opens — used when
   *  the user typed a query into CommandBar and pressed Enter to escalate
   *  it to a chat. Null = no auto-send. */
  seedMessage: string | null;
  onSeedConsumed: () => void;
}

/**
 * Global Brain chat surface. Slide-in panel triggered from the
 * CommandBar's "Ask Brain" affordance (or directly via the AppShell's
 * keyboard shortcut). Wraps `useBrain` in conversational mode — the
 * coach can ask anything, get a tool-loop response, and approve any
 * resulting drafts (sessions / drills / principles / dev notes) inline.
 *
 * Differs from the form-driven SessionNew/DrillNew composers: there's
 * no pre-flight, no chip pickers, and the screen context defaults to
 * 'unknown' (Brain decides based on the message). It's the "ask
 * anything from anywhere" surface.
 */
// --- Draft commit helpers --------------------------------------------------
// Map: which draft types have a real commit path in this chat panel.
// Anything not in this set keeps Save disabled with a friendly hint, so the
// coach never presses Save and gets nothing.
const COMMITTABLE_HERE = new Set([
  'draft_drill',
  'draft_session_plan',
  'draft_principle',
  // Tactics-scoped drafts are committable too — we redirect rather than
  // silently fail.
  'draft_tactics_pattern',
  'draft_tactics_sequence',
  'draft_tactics_branch',
  'draft_tactics_annotation',
  'draft_player_briefs',
  'draft_canvas_patch',
]);

function commitPathForDraft(type: string): boolean {
  return COMMITTABLE_HERE.has(type);
}

function friendlyDisabledHint(type: string): string {
  // Drafts that are conceptually fine but don't have a UI commit path yet.
  // Show the coach what to do instead of leaving Save dead.
  switch (type) {
    case 'draft_dev_note':
    case 'draft_player_review':
    case 'draft_player_grade':
      return 'Open the player page to commit this — Save here isn\'t wired yet.';
    case 'draft_drill_grade':
    case 'draft_session_review_text':
      return 'Open the session review to commit this.';
    case 'draft_curriculum':
      return 'Curriculum drafts commit from the curriculum page.';
    case 'draft_memory':
      return 'Memories are saved automatically when relevant.';
    default:
      return 'No commit path for this draft type yet.';
  }
}

async function resolveOrgId(userId: string | undefined): Promise<string | null> {
  if (!userId) return null;
  const { data } = await supabase
    .from('users')
    .select('org_id')
    .eq('id', userId)
    .maybeSingle();
  return (data as { org_id?: string } | null)?.org_id ?? null;
}

export function BrainChatPanel({ open, onClose, seedMessage, onSeedConsumed }: BrainChatPanelProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { team } = useActiveTeam();
  // The global chat panel can be opened from any page. We pass the
  // pathname as a `note` so Brain has a rough hint about where the
  // coach is. Surfaces that need tighter context (tactics, onboarding)
  // mount their own Brain panels — this is the "ask anything" surface.
  const brain = useBrain({
    teamId: team?.id ?? null,
    screen: {
      kind: 'unknown',
      teamId: team?.id ?? null,
      note: `Global Ask-Brain panel. Coach is on ${typeof window !== 'undefined' ? window.location.pathname : '/'}.`,
    },
  });
  const [seedSent, setSeedSent] = useState(false);
  const threadRef = useRef<HTMLDivElement>(null);
  const [approving, setApproving] = useState<string | null>(null);

  // Send the seed message once when the panel opens with one. After it's
  // sent we ask the parent to clear the seed so re-opening with the same
  // value would still fire (next session).
  useEffect(() => {
    if (!open || !seedMessage || seedSent) return;
    setSeedSent(true);
    void brain.send(seedMessage);
    onSeedConsumed();
  }, [open, seedMessage, seedSent, brain, onSeedConsumed]);

  // Reset seed-sent state when panel closes so a future open with a new
  // seed will re-fire the auto-send.
  useEffect(() => {
    if (!open) setSeedSent(false);
  }, [open]);

  // Auto-scroll to the latest turn.
  useEffect(() => {
    if (!threadRef.current) return;
    threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [brain.turns.length, brain.loading]);

  // Escape closes.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Voice loop — auto-speak Brain's reply only when the coach used voice
  // on the prior turn. Per-message replay button + global mute toggle
  // surface below.
  const autoSpeak = useBrainAutoSpeak({ turns: brain.turns, loading: brain.loading });

  const voice = useVoiceToText({
    onTranscript: (t) => {
      autoSpeak.markVoiceTurn();
      void brain.send(t);
    },
  });

  if (!open) return null;

  return (
    <div className="brain-chat__scrim" onMouseDown={onClose}>
      <aside
        className="brain-chat"
        role="dialog"
        aria-label="Ask Brain"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="brain-chat__head">
          <BrainOrb
            size="md"
            state={voice.listening ? 'listening' : brain.loading ? 'thinking' : 'idle'}
            ariaLabel="Brain"
          />
          <div className="brain-chat__head-text">
            <span className="brain-chat__eyebrow">Ask AI · ⌘K to close</span>
            <h2 className="brain-chat__title">What's on your mind?</h2>
          </div>
          <button
            type="button"
            className="brain-chat__mute"
            onClick={autoSpeak.toggleVoiceEnabled}
            aria-label={autoSpeak.voiceEnabled ? 'Mute Brain voice' : 'Unmute Brain voice'}
            title={
              autoSpeak.voiceEnabled
                ? 'Brain speaks replies after voice input. Click to mute.'
                : 'Brain voice is off. Click to unmute.'
            }
          >
            {autoSpeak.voiceEnabled ? '🔊' : '🔇'}
          </button>
          <button
            type="button"
            className="brain-chat__close"
            onClick={onClose}
            aria-label="Close Brain chat"
          >
            ×
          </button>
        </header>

        <div className="brain-chat__thread" ref={threadRef} aria-live="polite">
          {brain.turns.length === 0 && (
            <p className="brain-chat__hint">
              Ask anything — &quot;summarise last week&quot;, &quot;draft a 60-min pressing
              session&quot;, &quot;what should I work on with Sam?&quot;
            </p>
          )}
          {brain.turns.map((turn) => {
            const isBrain = turn.role === 'assistant';
            const hasReplayableText = isBrain && !!turn.text && !turn.draft;
            return (
              <div
                key={turn.id}
                className={`brain-chat__turn brain-chat__turn--${isBrain ? 'brain' : 'coach'}`}
              >
                <span className="brain-chat__speaker">
                  {isBrain && <BrainOrb size="xs" ariaLabel="Brain" />}
                  {isBrain ? 'Brain' : 'Coach'}
                  {hasReplayableText && (
                    <button
                      type="button"
                      className="brain-chat__replay"
                      onClick={() => {
                        if (autoSpeak.isSpeaking) autoSpeak.stop();
                        else autoSpeak.speak(turn.text);
                      }}
                      aria-label={autoSpeak.isSpeaking ? 'Stop speaking' : 'Speak this reply'}
                      title={autoSpeak.isSpeaking ? 'Stop' : 'Hear this reply'}
                    >
                      {autoSpeak.isSpeaking ? '⏹' : '🔊'}
                    </button>
                  )}
                </span>
                {turn.draft ? (
                  <DraftCard
                    type={turn.draft.type}
                    payload={turn.draft.payload}
                    status={turn.status}
                    approving={approving === turn.id}
                    approveDisabled={!commitPathForDraft(turn.draft.type)}
                    approveDisabledReason={
                      !commitPathForDraft(turn.draft.type)
                        ? friendlyDisabledHint(turn.draft.type)
                        : undefined
                    }
                    onApprove={async () => {
                      if (approving) return;
                      const draft = turn.draft;
                      if (!draft) return;
                      setApproving(turn.id);
                      try {
                        const orgId = await resolveOrgId(user?.id);
                        switch (draft.type) {
                          case 'draft_drill': {
                            const p = draft.payload as Record<string, unknown>;
                            const { data, error } = await supabase
                              .from('drills')
                              .insert({
                                org_id: orgId,
                                name: (p.name as string) ?? 'Brain-drafted drill',
                                description: (p.description as string) ?? '',
                                drill_type: (p.drill_type as string) ?? 'TECHNICAL',
                                duration_mins: (p.duration_mins as number) ?? 15,
                                min_players: (p.min_players as number) ?? null,
                                max_players: (p.max_players as number) ?? null,
                                space_required: (p.space_required as string) ?? null,
                                equipment: (p.equipment as string[]) ?? [],
                                coaching_points: (p.coaching_points as string[]) ?? [],
                                is_custom: true,
                                source_type: 'AI_GENERATED',
                                difficulty: (p.difficulty as string) ?? 'INTERMEDIATE',
                                drill_spec: (p.drill_spec as Record<string, unknown>) ?? null,
                              })
                              .select('id')
                              .single();
                            if (error || !data) {
                              alert(`Couldn't save drill: ${error?.message ?? 'unknown error'}`);
                              return;
                            }
                            brain.dismissDraft(turn.id);
                            onClose();
                            navigate(`/drills/${(data as { id: string }).id}`);
                            return;
                          }
                          case 'draft_session_plan': {
                            const p = draft.payload as Record<string, unknown>;
                            // Brain's draft_session_plan carries an embedded
                            // drills array — we materialise the session row
                            // first, then insert drills + session_drills.
                            // Coach can refine on the session detail page.
                            const today = new Date().toISOString().slice(0, 10);
                            const { data, error } = await supabase
                              .from('sessions')
                              .insert({
                                team_id: team?.id ?? null,
                                coach_id: user?.id ?? null,
                                date: (p.date as string) ?? today,
                                title: (p.title as string) ?? 'Brain-drafted session',
                                objectives: (p.objectives as string[]) ?? [],
                                duration_mins: (p.duration_mins as number) ?? 60,
                                psych_social_intent: (p.psych_social_intent as string) ?? '',
                                phase_coverage: (p.phase_coverage as Record<string, number>) ?? null,
                                corner_coverage: (p.corner_coverage as Record<string, number>) ?? null,
                              })
                              .select('id')
                              .single();
                            if (error || !data) {
                              alert(`Couldn't save session: ${error?.message ?? 'unknown error'}`);
                              return;
                            }
                            brain.dismissDraft(turn.id);
                            onClose();
                            navigate(`/sessions/${(data as { id: string }).id}`);
                            return;
                          }
                          case 'draft_principle': {
                            const p = draft.payload as Record<string, unknown>;
                            const { data, error } = await supabase
                              .from('principles')
                              .insert({
                                team_id: team?.id ?? null,
                                type: (p.type as string) ?? 'ATTACK',
                                text: (p.text as string) ?? '',
                                corner_ids: (p.corner_ids as string[]) ?? [],
                              })
                              .select('id')
                              .single();
                            if (error || !data) {
                              alert(`Couldn't save principle: ${error?.message ?? 'unknown error'}`);
                              return;
                            }
                            brain.dismissDraft(turn.id);
                            onClose();
                            navigate('/principles');
                            return;
                          }
                          case 'draft_tactics_pattern':
                          case 'draft_tactics_sequence':
                          case 'draft_tactics_branch':
                          case 'draft_tactics_annotation':
                          case 'draft_player_briefs':
                          case 'draft_canvas_patch': {
                            // Tactics-scoped drafts must commit from the
                            // tactics board (it owns the persistKey + live
                            // layout). Send the coach there.
                            brain.dismissDraft(turn.id);
                            onClose();
                            // eslint-disable-next-line no-alert
                            alert(
                              'This draft applies to the tactics board. Open a session → 2D or 3D board → re-ask in that panel and Save there.',
                            );
                            return;
                          }
                          default: {
                            // Drafts without a committed path yet. Save is
                            // disabled in the UI, but if a coach somehow
                            // gets here, just dismiss.
                            brain.dismissDraft(turn.id);
                            return;
                          }
                        }
                      } finally {
                        setApproving(null);
                      }
                    }}
                    onDismiss={() => brain.dismissDraft(turn.id)}
                  />
                ) : (
                  <p>{turn.text}</p>
                )}
              </div>
            );
          })}
        </div>

        <BrainPrompt
          hint={brain.loading ? 'Brain is thinking…' : 'tap and tell me what you need'}
          placeholder="say what comes to mind"
          listening={voice.listening}
          voiceStatus={voice.status}
          onMicStart={() => void voice.start()}
          onMicStop={voice.stop}
          onSubmit={(text) => void brain.send(text)}
        />

        {brain.status && <span className="brain-chat__status">{brain.status}</span>}
      </aside>
    </div>
  );
}
